import { faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { Button, JoonUIColor, Typography } from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useCheckoutRedirect } from "./useCheckoutRedirect"
import { CloseButton } from "../../components/closeButton/CloseButton"
import SlideUpModal from "../../components/slideUpModal/SlideUpModal"
import useIsCoachingAvailable from "../../hooks/useIsCoachingAvailable"
import { useUserQuery } from "../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"
import useSkipCoachingStep from "../coaching/useSkipCoachingStep"

interface QuarterlyOfferModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  isQuarterlyPlan: boolean
  setIsQuarterlyPlan: (isQuarterlyPlan: boolean) => void
}

export const useQuarterlyOfferModalStore = create<QuarterlyOfferModalStore>(
  (set) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true, isQuarterlyPlan: true }),
    onClose: () => set({ isOpen: false, isQuarterlyPlan: false }),
    isQuarterlyPlan: false,
    setIsQuarterlyPlan: (isQuarterlyPlan) => set({ isQuarterlyPlan }),
  })
)

const QuarterlyOfferModal = () => {
  const { isOpen, onClose, isQuarterlyPlan, setIsQuarterlyPlan } =
    useQuarterlyOfferModalStore()
  const { isLoadingCheckoutFn, redirectToFamilyCheckout } =
    useCheckoutRedirect()
  const { user } = useUserQuery()
  const { t } = useTranslation()
  const isCoachingAvailable = useIsCoachingAvailable()
  const skipCoachingStep = useSkipCoachingStep()

  const router = useRouter()

  const subOffering = user?.sub_offering as string | null | undefined
  const isNoFreeTrial =
    subOffering === "quarterly-0" || subOffering === "standard-0"

  const handleClose = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_SKIP)
    localStorage.removeItem(localStorageItems.therapistCode)
    // Skipping coaching for a non-English user still has to run the rest of
    // onboarding, so hand off to the same skip the coaching landing page uses.
    if (isCoachingAvailable) router.push("/coaching")
    else skipCoachingStep()
    onClose()
  }

  const handleContinue = () => {
    localStorage.removeItem(localStorageItems.therapistCode)
    localStorage.setItem(localStorageItems.stripeFromPaywall, "true")

    if (isQuarterlyPlan)
      trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_START_QUARTERLY)
    else trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_START_TRIAL)

    if (isNoFreeTrial) {
      setIsQuarterlyPlan(false)
      redirectToFamilyCheckout("default")
    } else {
      redirectToFamilyCheckout()
    }
  }

  if (isNoFreeTrial) {
    return (
      <SlideUpModal
        isOpen={isOpen}
        onClose={handleClose}
        size="fit-content"
        backgroundType="overlay"
      >
        <div className="w-full p-4 flex flex-col justify-start items-start gap-4">
          <CloseButton onClick={handleClose} />
          <Typography variant="h4">
            {t("PAYWALL_QUARTERLY_SPECIAL_OFFER")}
          </Typography>
          <div className="w-full flex flex-col justify-start items-start">
            <Typography variant="bodySmall">
              {t("PAYWALL_QUARTERLY_NOT_READY_UPFRONT")}
            </Typography>
            <Typography variant="bodyBold">
              {t("PAYWALL_QUARTERLY_START_TRIAL_INSTEAD")}
            </Typography>
          </div>
          <div className="w-full p-3 rounded-lg shadow-low-bottom outline outline-2 outline-borderAccent flex flex-col justify-start items-start gap-2">
            <div className="w-full flex justify-between items-center">
              <Typography variant="h4">
                {t("PAYWALL_QUARTERLY_ANNUAL_PLAN")}
              </Typography>
              <Typography variant="h4">$89.99</Typography>
            </div>
            <div className="w-full flex justify-start items-center gap-1">
              <Typography variant="bodySmall">
                {t("PAYWALL_QUARTERLY_FREE_7_DAYS")}
              </Typography>
              <div className="px-1 py-0.5 bg-viridian-400 rounded-md flex justify-center items-center">
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.inverted}
                >
                  {t("PAYWALL_QUARTERLY_FREE_TRIAL_BADGE")}
                </Typography>
              </div>
            </div>
          </div>
          <Button
            buttonType="primary"
            size="large"
            onClick={handleContinue}
            text={t("PAYWALL_QUARTERLY_CONTINUE_TRIAL")}
            rightIcon={faChevronRight}
            fullWidth
            isLoading={isLoadingCheckoutFn}
          />
        </div>
      </SlideUpModal>
    )
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={handleClose}
      size="fit-content"
      backgroundType="overlay"
    >
      <div className="w-full p-4 flex flex-col justify-start items-start gap-4">
        <CloseButton onClick={handleClose} />
        <Typography variant="h4">
          {t("PAYWALL_QUARTERLY_SPECIAL_OFFER")}
        </Typography>
        <div className="w-full flex flex-col justify-start items-start">
          <Typography variant="bodySmall">
            {t("PAYWALL_QUARTERLY_NOT_READY_YEAR")}
          </Typography>
          <Typography variant="bodyBold">
            {t("PAYWALL_QUARTERLY_START_QUARTERLY_INSTEAD")}
          </Typography>
        </div>
        <button onClick={() => setIsQuarterlyPlan(true)} className="w-full">
          <div
            className={`w-full p-3 rounded-lg shadow-low-bottom outline flex flex-col justify-start items-start gap-2 ${
              isQuarterlyPlan
                ? "outline-2 outline-borderAccent"
                : "outline-1 outline-borderPrimary"
            }`}
          >
            <div className="w-full flex justify-between items-center">
              <Typography variant="h4">
                {t("PAYWALL_QUARTERLY_QUARTERLY_PLAN")}
              </Typography>
              <Typography variant="h4">$39.99</Typography>
            </div>
            <Typography variant="bodySmall">
              {t("PAYWALL_QUARTERLY_PRICE_QUARTERLY")}
            </Typography>
          </div>
        </button>
        <button onClick={() => setIsQuarterlyPlan(false)} className="w-full">
          <div
            className={`w-full p-3 rounded-lg shadow-low-bottom outline flex flex-col justify-start items-start gap-2 ${
              !isQuarterlyPlan
                ? "outline-2 outline-borderAccent"
                : "outline-1 outline-borderPrimary"
            }`}
          >
            <div className="w-full flex justify-between items-center">
              <Typography variant="h4">
                {t("PAYWALL_QUARTERLY_ANNUAL_PLAN")}
              </Typography>
              <Typography variant="h4">$89.99</Typography>
            </div>
            <div className="w-full flex justify-start items-center gap-1">
              <Typography variant="bodySmall">
                {t("PAYWALL_BUNDLING_APP_PRICE")}
              </Typography>
              <div className="px-1 py-0.5 bg-viridian-400 rounded-md flex justify-center items-center">
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.inverted}
                >
                  {t("PAYWALL_QUARTERLY_SAVINGS")}
                </Typography>
              </div>
            </div>
          </div>
        </button>
        <Button
          buttonType="primary"
          size="large"
          onClick={handleContinue}
          text={
            isQuarterlyPlan
              ? t("PAYWALL_QUARTERLY_CONTINUE_QUARTERLY")
              : t("PAYWALL_QUARTERLY_CONTINUE_ANNUAL")
          }
          rightIcon={faChevronRight}
          fullWidth
          isLoading={isLoadingCheckoutFn}
        />
      </div>
    </SlideUpModal>
  )
}

export default QuarterlyOfferModal
